import React, { createContext, useContext, useCallback, useEffect, useRef, useReducer } from 'react';
import {
  ShortcutDefinition,
  ShortcutKey,
  Command,
  CommandCategory,
  KeyboardShortcutsContextValue,
} from '@/types/keyboard-shortcuts';

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextValue | null>(null);

const isInputElement = (element: Element | null): boolean => {
  if (!element) return false;
  const tagName = element.tagName.toLowerCase();
  const isInput = tagName === 'input' || tagName === 'textarea' || tagName === 'select';
  const isContentEditable = element.getAttribute('contenteditable') === 'true';
  return isInput || isContentEditable;
};

// Key symbol mappings
const KEY_SYMBOLS: Record<string, string> = {
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  Enter: '↵',
  Escape: 'Esc',
  Backspace: '⌫',
  Delete: '⌦',
  Tab: '⇥',
  ' ': 'Space',
};

export const formatShortcut = (keys: ShortcutKey, isMac = false): string[] => {
  const parts: string[] = [];
  const modifiers = keys.modifiers || [];

  if (modifiers.includes('ctrl') || modifiers.includes('meta')) {
    parts.push(isMac ? '⌘' : 'Ctrl');
  }
  if (modifiers.includes('alt')) {
    parts.push(isMac ? '⌥' : 'Alt');
  }
  if (modifiers.includes('shift')) {
    parts.push(isMac ? '⇧' : 'Shift');
  }

  // Use symbol if available, otherwise uppercase single chars or use as-is
  const keySymbol = KEY_SYMBOLS[keys.key]
    ?? (keys.key.length === 1 ? keys.key.toUpperCase() : keys.key);
  parts.push(keySymbol);

  return parts;
};

const matchesShortcut = (event: KeyboardEvent, shortcut: ShortcutKey): boolean => {
  const key = event.key.toLowerCase();
  const shortcutKey = shortcut.key.toLowerCase();

  if (key !== shortcutKey) return false;

  const modifiers = shortcut.modifiers || [];
  const needsCtrlOrMeta = modifiers.includes('ctrl') || modifiers.includes('meta');

  if (needsCtrlOrMeta) {
    if (!(event.ctrlKey || event.metaKey)) return false;
  } else {
    if (event.ctrlKey || event.metaKey) return false;
  }

  const needsAlt = modifiers.includes('alt');
  if (needsAlt !== event.altKey) return false;

  const needsShift = modifiers.includes('shift');
  if (needsShift !== event.shiftKey) return false;

  return true;
};

export function KeyboardShortcutsProvider({ children }: { children: React.ReactNode }) {
  const shortcutsRef = useRef<Map<string, ShortcutDefinition>>(new Map());
  const commandsRef = useRef<Map<string, Command>>(new Map());
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const isInputFocused = useCallback(() => {
    return isInputElement(document.activeElement);
  }, []);

  const registerShortcut = useCallback((shortcut: ShortcutDefinition) => {
    shortcutsRef.current.set(shortcut.id, shortcut);
    forceUpdate();
    return () => {
      shortcutsRef.current.delete(shortcut.id);
      forceUpdate();
    };
  }, []);

  const registerCommand = useCallback((command: Command) => {
    commandsRef.current.set(command.id, command);
    if (command.keys) {
      shortcutsRef.current.set(command.id, {
        ...command,
        keys: command.keys,
      } as ShortcutDefinition);
    }
    forceUpdate();
    return () => {
      commandsRef.current.delete(command.id);
      shortcutsRef.current.delete(command.id);
      forceUpdate();
    };
  }, []);

  const unregisterShortcut = useCallback((id: string) => {
    shortcutsRef.current.delete(id);
    forceUpdate();
  }, []);

  const unregisterCommand = useCallback((id: string) => {
    commandsRef.current.delete(id);
    shortcutsRef.current.delete(id);
    forceUpdate();
  }, []);

  const getShortcutsByCategory = useCallback((category: CommandCategory) => {
    return Array.from(shortcutsRef.current.values()).filter((s) => s.category === category);
  }, []);

  const getAllCommands = useCallback(() => {
    return Array.from(commandsRef.current.values());
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Allow Escape even in inputs (for closing dialogs)
      if (isInputFocused() && event.key !== 'Escape') {
        return;
      }

      for (const shortcut of shortcutsRef.current.values()) {
        if (matchesShortcut(event, shortcut.keys)) {
          const isEnabled =
            typeof shortcut.enabled === 'function' ? shortcut.enabled() : shortcut.enabled !== false;

          if (isEnabled) {
            event.preventDefault();
            event.stopPropagation();
            shortcut.action();
            return;
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isInputFocused]);

  const value: KeyboardShortcutsContextValue = {
    shortcuts: shortcutsRef.current,
    commands: commandsRef.current,
    registerShortcut,
    registerCommand,
    unregisterShortcut,
    unregisterCommand,
    isInputFocused,
    getShortcutsByCategory,
    getAllCommands,
  };

  return (
    <KeyboardShortcutsContext.Provider value={value}>{children}</KeyboardShortcutsContext.Provider>
  );
}

export function useKeyboardShortcuts() {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error('useKeyboardShortcuts must be used within KeyboardShortcutsProvider');
  }
  return context;
}
